<script setup lang="ts">
import { computed, useSlots } from 'vue'

import LoadingSpinner from '../LoadingSpinner.vue'

interface ButtonPrimaryProps {
  label: string
  disabled?: boolean | undefined
  loading?: boolean | undefined
  loadingSkeleton?: boolean | undefined
  small?: boolean | undefined
  type?: 'button' | 'submit' | undefined
}

const props = defineProps<ButtonPrimaryProps>()

const isDisabled = computed(() => props.disabled || props.loading)
const isSkeleton = computed(() => props.loadingSkeleton)
const hasIcon = useSlots()['icon']
const hasLabel = computed(() => props.label.trim().length > 0)

defineSlots<{
  icon(): string
}>()
</script>

<template>
  <button
    class="no-edge-dark relative inline-flex h-[40px] items-center justify-center rounded-[3.125rem] px-6 shadow-md"
    :class="[
      isSkeleton
        ? 'animate-pulse bg-black-200 text-transparent'
        : isDisabled
          ? 'bg-black-100 text-black-400 hover:cursor-not-allowed'
          : 'bg-lochmara-600 text-white hover:cursor-pointer hover:bg-lochmara-700',
      props.small ? 'py-2 text-sm' : 'py-3 text-base',
      hasIcon && hasLabel ? 'gap-2' : '',
    ]"
    :disabled="isDisabled"
    :type="type ?? 'button'"
  >
    <span
      v-if="hasIcon"
      class="transition-opacity duration-300"
      :class="loading ? 'invisible' : ''"
      :style="{ opacity: isSkeleton ? 0 : 1 }"
    >
      <slot name="icon"></slot>
    </span>

    <span
      v-if="hasLabel"
      class="transition-opacity duration-300"
      :class="loading ? 'invisible' : ''"
      :style="{ opacity: isSkeleton ? 0 : 1 }"
    >
      {{ props.label }}
    </span>
    <LoadingSpinner v-if="loading" class="margin-auto absolute" size="1rem" />
  </button>
</template>
